import React, { useState, useRef, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
  useTheme,
} from '@mui/material';

import { API_CONFIG } from '../../services/apiConfig';
import { getBackendOrigin, redirectToLogin, deferRedirect } from '../../utils/backendUrl';
import { useAuth } from '../../contexts/AuthContext';
import CoreInput from '../ui/core/CoreInput';

interface ChangePasswordDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

const ChangePasswordDialog: React.FC<ChangePasswordDialogProps> = ({ isOpen, onClose }) => {
  const theme = useTheme();
  const { user, loading } = useAuth();
  const closeTimerRef = useRef<number | null>(null);

  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    setOldPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setSubmitting(false);
    setMessage(null);
    setError(null);
  };

  const handleClose = () => {
    if (closeTimerRef.current) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
    reset();
    onClose();
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage(null);
    setError(null);
    if (loading || !user) {
      setError('You must be signed in to change your password.');
      return;
    }
    // We no longer require Amplify tokens; backend handles Cognito interaction
    if (!oldPassword || !newPassword) {
      setError('Please enter your current and new password.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('New password and confirmation do not match.');
      return;
    }
    try {
      setSubmitting(true);
      const apiBase = API_CONFIG.BASE_URL;
      const resp = await fetch(`${apiBase}/auth/change-password/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ old_password: oldPassword, new_password: newPassword })
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        // If backend requests reauthentication, redirect to backend /login
        if (resp.status === 409 && (data?.error === 'reauthentication_required')) {
          redirectToLogin();
          return;
        }
        const errMsg = (data && (data.message || data?.details?.suggestion || data?.details?.message || data?.details?.cognito_error || data.error))
          || `Failed to change password (HTTP ${resp.status})`;
        setError(errMsg);
      } else {
        setMessage('Password changed successfully.');
        setOldPassword(''); setNewPassword(''); setConfirmPassword('');
        closeTimerRef.current = window.setTimeout(() => {
          handleClose();
        }, 800);
      }
    } catch (err: any) {
      const msg = err?.message || 'Failed to update password.';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    return () => {
      if (closeTimerRef.current) {
        window.clearTimeout(closeTimerRef.current);
        closeTimerRef.current = null;
      }
    };
  }, []);

  const handleKeyDown = React.useCallback((e: KeyboardEvent) => {
    if (e.key === "Enter") {
      onSubmit(e as any);
    }
  }, []);

  React.useEffect(() => {
    if (!isOpen) return;

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, handleKeyDown]);

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      maxWidth='xs'
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle
        sx={{
          pb: 2,
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Typography
          sx={{
            fontSize: "19px",
            fontWeight: 600,
            color: theme.palette.text.primary,
          }}
        >
          Change Password
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ px: 3, py: 2 }}>
        {loading || !user ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, paddingTop: 2 }}>
            <Typography
              variant="body2"
              sx={{
                color: theme.palette.text.secondary,
              }}
            >
              You need to sign in to change your password.
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                onClick={() => {
                  deferRedirect(`${getBackendOrigin()}/login`);
                }}
                sx={{
                  backgroundColor: theme.palette.text.primary,
                  color: theme.palette.background.paper,
                  '&:hover': {
                    backgroundColor: theme.palette.text.primary,
                    opacity: 0.9,
                  },
                }}
              >
                Sign in
              </Button>
            </Box>
          </Box>
        ) : (
          <Box
            component="form"
            onSubmit={onSubmit}
            sx={{ display: 'flex', flexDirection: 'column', gap: 2, paddingTop: 2 }}
          >
            <CoreInput
              label="Current password"
              type="password"
              value={oldPassword}
              onChange={(e) => setOldPassword(e.target.value)}
              autoComplete="current-password"
              required
            />

            <CoreInput
              label="New password"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              autoComplete="new-password"
              required
            />

            <CoreInput
              label="Confirm new password"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              autoComplete="new-password"
              required
            />

            {error && (
              <Alert severity="error" sx={{ mt: 1 }}>
                {error}
              </Alert>
            )}

            {message && (
              <Alert severity="success" sx={{ mt: 1 }}>
                {message}
              </Alert>
            )}
          </Box>
        )}
      </DialogContent>

      {
        loading || !user ? null :
          <DialogActions
              sx={{
                px: 3,
                py: 2,
                gap: 1.5,
                borderTop: `1px solid ${theme.palette.divider}`,
              }}
            >
              <Button
                type="button"
                onClick={handleClose}
                variant="outlined"
              >
                Cancel
              </Button>
              
              <Button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  // Call onSubmit with a synthetic event since the button is outside the form
                  onSubmit(e as React.FormEvent);
                }}
                disabled={submitting}
                variant="contained"
              >
                {submitting ? 'Saving…' : 'Save password'}
              </Button>
            </DialogActions>
      }
    </Dialog>
  );
};

export default ChangePasswordDialog;


